/**
 * AgentStreamDock — a VSCode-terminal-style docked panel for agent streams/logs.
 *
 * Replaces the room's single tiny inline detail pane: click an agent → it opens as a TAB; many tabs
 * (each live stream OR static log); closeable + re-openable; draggable resize; horizontal SPLIT into
 * two panes; the SAME agent can live in both panes; per-tab status badge + Stop.
 *
 * REUSABLE / host-agnostic — takes data by prop (the per-agent ``agentRoster`` slice + a
 * "requestedAgentId/nonce" open signal + a stop callback), owns only its own tab/pane layout. Works
 * because the room store is already per-agent (agentRoomReducer: roster[agentId] holds each agent's
 * status/text/builderLines/builderCompletion), so N tabs — even the same agentId in two panes —
 * render off the same memoized slice concurrently. No backend/store change.
 *
 * Props:
 *   agentRoster       Array of agent rows ({agentId,label,status,isBuilder,builderLines,
 *                     builderCompletion,text,usage,runId}). Host passes the memoized store slice.
 *   networkId,poolId,taskId  threaded to the leaf renderers (log fetch / usage).
 *   requestedAgentId  agentId the host wants opened/focused (from a tree/row click).
 *   requestedNonce    bumps on each click so re-clicking a CLOSED tab re-opens it (same id).
 *   onStopRun(runId)  stop a running builder (= builderControls.stopBuilder).
 *   stoppingSet       Set of runIds currently stopping (per-tab spinner).
 *   onCollapse        optional: host toggle to hide the dock.
 *   storageKey        localStorage namespace for height (default 'stepflow:agent-dock-height').
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X as XIcon, SplitSquareHorizontal, ChevronDown, StopCircle, Loader2 } from 'lucide-react';
import { useResizable } from '@stepflow/ui-shared/hooks/useResizable.js';
import DockTab, { DockTabBadge, dockTabStatus } from './DockTab.jsx';
import PromptCaptureLink from '../turn/PromptCaptureLink.jsx';

/**
 * Address the prompt-capture (the "Context" affordance) for a dock tab's agent row — the SAME keys
 * the leaf renderers use so a capture written by the run resolves here:
 *   • builder → (source:'builder', agent:agentId, run:runId)          — matches BuilderStreamTerminal
 *   • worker  → (source:'worker', agent:label||agentId, pool, task)   — matches AgentResponsePanel
 * PromptCaptureLink self-gates (renders nothing until it has an addressable key), so a still-queued
 * row simply shows no Context button.
 */
export function dockCaptureProps(row, poolId, taskId) {
  const r = row || {};
  if (r.isBuilder) {
    return { source: 'builder', agent: r.agentId, run: r.runId };
  }
  return { source: 'worker', agent: r.label || r.agentId, pool: poolId, task: r.runId || taskId };
}

const _HEIGHT_KEY_DEFAULT = 'stepflow:agent-dock-height';
let _paneSeq = 0;
const _nextPaneId = () => { _paneSeq += 1; return `pane-${_paneSeq}`; };

export default function AgentStreamDock({
  agentRoster = [],
  networkId,
  poolId,
  taskId,
  requestedAgentId = null,
  requestedNonce = 0,
  onStopRun,
  stoppingSet,
  onCollapse,
  storageKey = _HEIGHT_KEY_DEFAULT,
}) {
  // Per-agent lookup — the shared slice. Same object every render (memoized by the host), so two
  // panes reading the same agentId get identical live state.
  const rosterById = useMemo(() => {
    const m = {};
    for (const r of (Array.isArray(agentRoster) ? agentRoster : [])) {
      if (r && r.agentId) m[String(r.agentId)] = r;
    }
    return m;
  }, [agentRoster]);

  // LAYOUT MODEL: panes → tabs (agentIds) + active. One pane by default; a second appears on split.
  // A "tab" is just an agentId string — moving/duplicating across panes is array ops, not stream
  // re-plumbing, and the same agentId in two panes both read rosterById[id].
  const [panes, setPanes] = useState(() => [{ id: _nextPaneId(), tabs: [], activeTabId: null }]);
  const [focusedPaneId, setFocusedPaneId] = useState(null);

  // Dock height (persisted) + collapse.
  // The dock sits at the BOTTOM and its resize grip is on its TOP rim, so dragging UP must GROW it
  // (``edge: 'top'`` inverts the delta) — otherwise up/down feel backwards.
  const { height, onMouseDown } = useResizable({ minHeight: 120, defaultHeight: _initialHeight(storageKey), maxHeight: _maxHeight(), edge: 'top' });
  useEffect(() => {
    try { window.localStorage?.setItem(storageKey, String(Math.round(height))); } catch (_e) { /* ignore */ }
  }, [height, storageKey]);

  // OPEN/FOCUS a requested agent in the focused pane (or the first pane). Nonce-guarded so a
  // re-click of the SAME id re-opens it after a close (nonce changed → effect re-fires).
  const lastNonce = useRef(-1);
  useEffect(() => {
    if (!requestedAgentId || requestedNonce === lastNonce.current) return;
    lastNonce.current = requestedNonce;
    setPanes((prev) => {
      const targetId = focusedPaneId && prev.some((p) => p.id === focusedPaneId)
        ? focusedPaneId : prev[0]?.id;
      return prev.map((p) => {
        if (p.id !== targetId) return p;
        const tabs = p.tabs.includes(requestedAgentId) ? p.tabs : [...p.tabs, requestedAgentId];
        return { ...p, tabs, activeTabId: requestedAgentId };
      });
    });
    setFocusedPaneId((f) => f || null);
  }, [requestedAgentId, requestedNonce, focusedPaneId]);

  const closeTab = useCallback((paneId, agentId) => {
    setPanes((prev) => {
      let next = prev.map((p) => {
        if (p.id !== paneId) return p;
        const tabs = p.tabs.filter((t) => t !== agentId);
        const activeTabId = p.activeTabId === agentId ? (tabs[tabs.length - 1] || null) : p.activeTabId;
        return { ...p, tabs, activeTabId };
      });
      // Collapse an emptied SECOND pane back to single-pane (never drop the last pane).
      if (next.length > 1) next = next.filter((p, i) => i === 0 || p.tabs.length > 0);
      if (next.length === 0) next = [{ id: _nextPaneId(), tabs: [], activeTabId: null }];
      return next;
    });
  }, []);

  const focusTab = useCallback((paneId, agentId) => {
    setFocusedPaneId(paneId);
    setPanes((prev) => prev.map((p) => (p.id === paneId ? { ...p, activeTabId: agentId } : p)));
  }, []);

  // SPLIT — clone the focused pane's active tab into a new second pane (VSCode "split terminal").
  const splitHorizontal = useCallback(() => {
    setPanes((prev) => {
      if (prev.length >= 2) return prev;   // v1: max 2 panes
      const src = prev.find((p) => p.id === focusedPaneId) || prev[0];
      const seed = src?.activeTabId ? [src.activeTabId] : [];
      const newPane = { id: _nextPaneId(), tabs: seed, activeTabId: seed[0] || null };
      return [...prev, newPane];
    });
  }, [focusedPaneId]);

  // DRAG a tab across panes. On drop we ADD to the target pane (keep in source too → the same agent
  // can legitimately live in both; the user closes it from a pane if they don't want it there).
  const onTabDrop = useCallback((targetPaneId, agentId) => {
    if (!agentId) return;
    setPanes((prev) => prev.map((p) => {
      if (p.id !== targetPaneId) return p;
      const tabs = p.tabs.includes(agentId) ? p.tabs : [...p.tabs, agentId];
      return { ...p, tabs, activeTabId: agentId };
    }));
    setFocusedPaneId(targetPaneId);
  }, []);

  const totalTabs = panes.reduce((n, p) => n + p.tabs.length, 0);

  // ZERO footprint until a tab opens: no resting bar at all (it was a full-width strip of "click an agent" chrome —
  // the command deck's lower-left "Agent streams" toggle owns show/hide now; MODES.md §9). Expands when a tab opens.
  if (totalTabs === 0) return null;

  return (
    <div
      data-testid="agent-stream-dock"
      className="flex shrink-0 flex-col border-t border-gray-200 bg-white"
      style={{ height: `${Math.round(height)}px` }}
    >
      {/* Resize handle (drag the top edge) + dock header. */}
      <div
        role="separator"
        aria-orientation="horizontal"
        onMouseDown={onMouseDown}
        className="h-1.5 w-full cursor-row-resize bg-gray-100 hover:bg-indigo-200"
        title="Drag to resize"
      />
      <div className="flex shrink-0 items-center gap-2 border-b border-gray-100 px-2 py-1">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Agent streams</span>
        <span className="text-[10px] text-gray-400">{totalTabs} open</span>
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            data-testid="dock-split"
            onClick={splitHorizontal}
            disabled={panes.length >= 2 || totalTabs === 0}
            title="Split editor (open a second pane)"
            className="rounded p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-40"
          >
            <SplitSquareHorizontal className="h-3.5 w-3.5" aria-hidden />
          </button>
          {onCollapse ? (
            <button
              type="button"
              data-testid="dock-collapse"
              onClick={onCollapse}
              title="Hide the dock"
              className="rounded p-1 text-gray-500 hover:bg-gray-100"
            >
              <ChevronDown className="h-3.5 w-3.5" aria-hidden />
            </button>
          ) : null}
        </div>
      </div>

      {/* Panes row (1 or 2, side by side). */}
      <div className="flex min-h-0 flex-1 flex-row">
        {panes.map((pane, idx) => (
          <DockPane
            key={pane.id}
            pane={pane}
            isFocused={pane.id === (focusedPaneId || panes[0].id)}
            showLeftBorder={idx > 0}
            rosterById={rosterById}
            networkId={networkId}
            poolId={poolId}
            taskId={taskId}
            onStopRun={onStopRun}
            stoppingSet={stoppingSet}
            onFocusTab={focusTab}
            onCloseTab={closeTab}
            onTabDrop={onTabDrop}
          />
        ))}
      </div>
    </div>
  );
}

/** One pane: its own tab strip + the active tab's body. Drop target for cross-pane tab drag. */
function DockPane({
  pane, isFocused, showLeftBorder, rosterById, networkId, poolId, taskId,
  onStopRun, stoppingSet, onFocusTab, onCloseTab, onTabDrop,
}) {
  const [dragOver, setDragOver] = useState(false);
  const activeRow = pane.activeTabId ? rosterById[pane.activeTabId] : null;
  const activeStatus = activeRow ? dockTabStatus(activeRow, stoppingSet) : null;
  const canStop = !!activeRow && (activeStatus?.kind === 'streaming' || activeStatus?.kind === 'running');
  const stopping = !!activeRow?.runId && stoppingSet && typeof stoppingSet.has === 'function'
    && stoppingSet.has(activeRow.runId);

  // DONE-BUILDER sub-view: Output (claim-check the synthesizer folds) vs Logs (transcript). The toggle
  // lives in the EXISTING active-tab header (no extra row — save space); each view uses the full body.
  const _bc = activeRow?.builderCompletion;
  const isDoneBuilderRow = !!activeRow?.isBuilder && !!_bc && !!(_bc.logKey || _bc.eventsKey);
  const hasBuilderBoth = isDoneBuilderRow && !!activeRow?.text;
  const [builderView, setBuilderView] = useState('output');
  // Reset to Output when the active agent changes (so a new selection defaults to the deliverable).
  const _lastAgent = React.useRef(activeRow?.agentId);
  if (_lastAgent.current !== activeRow?.agentId) { _lastAgent.current = activeRow?.agentId; if (builderView !== 'output') setBuilderView('output'); }

  return (
    <div
      className={`flex min-h-0 min-w-0 flex-1 flex-col ${showLeftBorder ? 'border-l border-gray-200' : ''} ${isFocused ? '' : 'opacity-95'}`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const id = e.dataTransfer.getData('text/agent-id');
        if (id) onTabDrop(pane.id, id);
      }}
    >
      {/* Tab strip. */}
      <div className={`flex shrink-0 items-center gap-0.5 overflow-x-auto border-b px-1 py-0.5 ${dragOver ? 'bg-indigo-50 border-indigo-200' : 'border-gray-100'}`}>
        {pane.tabs.length === 0 ? (
          <span className="px-2 py-1 text-[11px] text-gray-400">Click an agent to open its stream</span>
        ) : pane.tabs.map((agentId) => {
          const row = rosterById[agentId];
          const st = dockTabStatus(row, stoppingSet);
          const label = row?.label || agentId;
          const active = pane.activeTabId === agentId;
          return (
            <div
              key={agentId}
              draggable
              onDragStart={(e) => e.dataTransfer.setData('text/agent-id', agentId)}
              onClick={() => onFocusTab(pane.id, agentId)}
              className={`group flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-t px-2 py-1 text-[11px] ${active ? 'bg-white font-medium text-gray-800 shadow-[inset_0_-2px_0_0_#6366f1]' : 'text-gray-500 hover:bg-gray-50'}`}
              data-testid={`dock-tab-${agentId}`}
              title={label}
            >
              <DockTabBadge kind={st.kind} label="" />
              <span className="max-w-[10rem] truncate">{label}</span>
              <button
                type="button"
                data-testid={`dock-tab-close-${agentId}`}
                onClick={(e) => { e.stopPropagation(); onCloseTab(pane.id, agentId); }}
                className="rounded p-0.5 text-gray-400 opacity-0 hover:bg-gray-200 hover:text-gray-700 group-hover:opacity-100"
                aria-label={`Close ${label}`}
              >
                <XIcon className="h-3 w-3" aria-hidden />
              </button>
            </div>
          );
        })}
      </div>

      {/* Active tab header (status + Stop) + body. */}
      {activeRow ? (
        <>
          <div className="flex shrink-0 items-center gap-2 border-b border-gray-100 bg-gray-50/60 px-2 py-1">
            <span className="truncate text-[11px] font-medium text-gray-700">{activeRow.label || activeRow.agentId}</span>
            {activeStatus ? <DockTabBadge kind={activeStatus.kind} label={activeStatus.label} /> : null}
            {/* Right-aligned actions: "Context" (the exact assembled prompt this agent was given —
                restored from the old inline agent-window header, lost when the dock leaves render
                with hideChrome/hideHeader) + Stop. */}
            {/* Output/Logs sub-view toggle — in THIS header (no extra row). Only for a done builder that
                has BOTH a claim-check output and a transcript; each view fills the whole body. */}
            {hasBuilderBoth ? (
              <div className="ml-2 flex items-center gap-0.5 rounded bg-gray-100 p-0.5" role="tablist">
                {[['output', 'Output'], ['logs', 'Logs']].map(([k, lbl]) => (
                  <button
                    key={k}
                    type="button"
                    role="tab"
                    aria-selected={builderView === k}
                    data-testid={`dock-subtab-${k}`}
                    onClick={() => setBuilderView(k)}
                    className={`rounded px-2 py-0.5 text-[10px] font-medium transition-colors ${
                      builderView === k ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                    }`}
                    title={k === 'output' ? 'The output the synthesizer folds' : 'The full run transcript'}
                  >
                    {lbl}
                  </button>
                ))}
              </div>
            ) : null}
            <div className="ml-auto flex items-center gap-0.5">
              <PromptCaptureLink networkId={networkId} {...dockCaptureProps(activeRow, poolId, taskId)} />
              {canStop && onStopRun ? (
                <button
                  type="button"
                  data-testid={`dock-stop-${activeRow.agentId}`}
                  onClick={() => activeRow.runId && onStopRun(activeRow.runId)}
                  disabled={stopping}
                  className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                  title="Stop this run"
                >
                  {stopping ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : <StopCircle className="h-3 w-3" aria-hidden />}
                  Stop
                </button>
              ) : null}
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-auto" data-testid={`dock-body-${activeRow.agentId}`}>
            {/* Key per (pane,agent) so the two mounts of the same agent stay isolated (chrome/scroll),
                mirroring the existing per-builder keying in AgentHierarchyPanel. */}
            <DockTab
              key={`${pane.id}:${activeRow.agentId}`}
              row={activeRow}
              networkId={networkId}
              poolId={poolId}
              taskId={taskId}
              builderView={builderView}
            />
          </div>
        </>
      ) : (
        <div className="flex min-h-0 flex-1 items-center justify-center text-[12px] text-gray-400">
          No tab open in this pane.
        </div>
      )}
    </div>
  );
}

function _initialHeight(key) {
  try {
    const v = Number(window.localStorage?.getItem(key));
    if (Number.isFinite(v) && v >= 120) return Math.min(v, _maxHeight());
  } catch (_e) { /* ignore */ }
  return 260;
}
function _maxHeight() {
  return typeof window !== 'undefined' ? Math.round(window.innerHeight * 0.7) : 600;
}
